/**
 * **Measure and sight between two ends** — the one fold `lvtt encounter measure`, `lvtt encounter
 * los` and the board's Measure tool share (`projects/measurement-tool.md`, rulings 3, 5 and 11).
 *
 * Distance is the engine's range count, {@link footprintDistance} (Chebyshev on the square grid: a
 * diagonal step counts 1, Core p.62), so a measure always equals `combatantRange` /
 * `combatantCellRange` for the same ends. An end on a unit binds to its whole footprint and measures
 * from its nearest edge (Core p.64); an end on a cell is that one cell.
 *
 * A unit end may also name the cell the pointer is on (`at`, the board's Measure tool): the ruler then
 * anchors on that cell, so `distance`, `nearest` and `counted` are cell-to-cell there, while `range`
 * and `sight` still read the whole footprint. Developer ruling 2026-09-28: the ruler snapping to the
 * nearest footprint cell looked arbitrary on Size 2+ units.
 */

import { type Cell, type Combatant, type CombatantId, type EncounterState, type World, encounterGridMode, tagCell, terrainVisibility, worldFor } from "@lancer-vtt/domain";
import type { LoadedOverlay } from "./loader";
import { alliedCoverCells } from "./seed";
import { footprintCells, footprintDistance } from "./spatialAnswerer";

/** One end of a measure: a unit (its footprint, the ruler anchored on `at` when given) or a bare cell. */
export type MeasureEnd = { unit: CombatantId; at?: Cell } | { cell: Cell };

export type SightReadout = ReturnType<typeof terrainVisibility>;

export type Measure = {
  /** The ruler's count: `counted.length`. Equals `range` unless an end is anchored with `at`. */
  distance: number;
  /** The engine's range between the two ends' whole footprints (`combatantRange` / `combatantCellRange`). */
  range: number;
  fromCells: Cell[];
  toCells: Cell[];
  /** The ruler's ends (`[from, to]`): an `at` cell, else the closest pair across the footprints, ties broken by key order. */
  nearest: [Cell, Cell];
  /** `line(nearest)` minus its start: one cell per counted space, so `counted.length === distance`. */
  counted: Cell[];
  sight: SightReadout;
};

type SightArgs = {
  state: EncounterState;
  from: Cell[];
  to: Cell[];
  fromId?: CombatantId;
  toId?: CombatantId;
  overlay?: LoadedOverlay;
};

/**
 * The sight fold: `terrainVisibility` across both footprints, excluding the `to` unit's own cover
 * cells, with every character that is hard cover for `to`'s allies (the Goliath's Guardian) counted
 * through the SAME `alliedCoverCells` the attack's to-hit folds. A cell end has no allies to cover.
 */
function sightBetween(args: SightArgs): SightReadout {
  const { state, from, to, fromId, toId, overlay } = args;
  return terrainVisibility({
    space: worldFor(state),
    terrain: state.terrain,
    from,
    to,
    edges: state.edges,
    combatants: state.combatants,
    excludeCoverFrom: toId,
    areaEffects: state.areaEffects,
    extraHardCoverCells: toId === undefined ? undefined : alliedCoverCells({ state, targetId: toId, attackerId: fromId ?? "", overlay }),
  });
}

/**
 * The line of sight + cover `lvtt encounter los` reports between two placed combatants. The attack
 * cover hatch reads the same geometry, so `los` and the attack agree.
 */
export function losBetween(args: { state: EncounterState; from: Combatant; to: Combatant; overlay: LoadedOverlay }): SightReadout {
  const { state, from, to, overlay } = args;
  const world = worldFor(state);
  return sightBetween({ state, from: footprintCells(world, from), to: footprintCells(world, to), fromId: from.id, toId: to.id, overlay });
}

function endCells(world: World<Cell>, state: EncounterState, end: MeasureEnd): Cell[] {
  return "unit" in end ? footprintCells(world, state.combatants[end.unit]) : [end.cell];
}

function nearestPair(world: World<Cell>, from: Cell[], to: Cell[]): [Cell, Cell] {
  let best: { pair: [Cell, Cell]; d: number; key: string } | null = null;
  for (const a of from) {
    for (const b of to) {
      const d = world.distance(a, b);
      const key = `${world.key(a)}|${world.key(b)}`;
      if (best === null || d < best.d || (d === best.d && key < best.key)) best = { pair: [a, b], d, key };
    }
  }
  return best!.pair;
}

/** Args for {@link measureBetween} (max-params-3 discipline, `CODE_STYLE.md`). */
export type MeasureArgs = { state: EncounterState; from: MeasureEnd; to: MeasureEnd; overlay?: LoadedOverlay };

/**
 * Measure from one end to the other: the engine's distance, the nearest cell pair, the counted
 * cells, and the sight readout. `null` when a unit end is unknown or unplaced.
 */
export function measureBetween(args: MeasureArgs): Measure | null {
  const { state, from, to, overlay } = args;
  const world = worldFor(state);
  // The world's geometry hands back untagged cells; stamp the grid so every cell out is a stored/wire `Cell`.
  const mode = encounterGridMode(state);
  const tag = (cells: Cell[]) => cells.map((c) => tagCell(mode, c));
  const fromCells = tag(endCells(world, state, from));
  const toCells = tag(endCells(world, state, to));
  const range = footprintDistance(world, fromCells, toCells);
  if (range === null) return null;
  const anchor = (end: MeasureEnd, cells: Cell[]) => ("unit" in end && end.at ? tag([end.at]) : cells);
  const nearest = nearestPair(world, anchor(from, fromCells), anchor(to, toCells));
  return {
    distance: world.distance(nearest[0], nearest[1]),
    range,
    fromCells,
    toCells,
    nearest,
    counted: tag(world.line(nearest[0], nearest[1]).slice(1)),
    sight: sightBetween({
      state,
      from: fromCells,
      to: toCells,
      fromId: "unit" in from ? from.unit : undefined,
      toId: "unit" in to ? to.unit : undefined,
      overlay,
    }),
  };
}
